import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from "@mui/lab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import NoteIcon from "@mui/icons-material/Note";
import InfoIcon from "@mui/icons-material/Info";
import useTranslation from "../../../shared/hooks/useTranslation";
import offerExecutionApi from "../api/offerExecutionApi";
import { ProgressUpdate, UpdateType } from "../../servicing/models/executionModels";
import ProgressUpdateForm from "./ProgressUpdateForm";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { addProgressUpdate } from "../customerOffersSlice";
import { getUiLocale } from "../../../shared/utils/relativeTime";

interface ProviderProgressTimelineProps {
  offerId: string;
}

const ProviderProgressTimeline: React.FC<ProviderProgressTimelineProps> = ({ offerId }) => {
  const { translate } = useTranslation(["serviceOffers", "servicing"]);
  const dispatch = useAppDispatch();
  const [updates, setUpdates] = useState<ProgressUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchUpdates = async () => {
    try {
      const data = await offerExecutionApi.getProgressUpdates(offerId);
      setUpdates(data);
    } catch (err) {
      console.error("Failed to fetch progress updates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, [offerId]);

  const handlePostUpdate = async (message: string) => {
    setSubmitting(true);
    try {
      await dispatch(addProgressUpdate({ offerId, message })).unwrap();
      await fetchUpdates();
    } catch (err) {
      console.error("Failed to post progress update:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const getUpdateIcon = (updateType: UpdateType) => {
    switch (updateType) {
      case UpdateType.StatusChange:
        return <PlayArrowIcon sx={{ fontSize: 16 }} />;
      case UpdateType.ProgressNote:
        return <NoteIcon sx={{ fontSize: 16 }} />;
      case UpdateType.SystemEvent:
        return <InfoIcon sx={{ fontSize: 16 }} />;
      default:
        return <InfoIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getUpdateColor = (updateType: UpdateType): "primary" | "secondary" | "grey" | "success" => {
    switch (updateType) {
      case UpdateType.StatusChange:
        return "success";
      case UpdateType.ProgressNote:
        return "primary";
      case UpdateType.SystemEvent:
        return "grey";
      default:
        return "grey";
    }
  };

  const getUpdateTypeLabel = (updateType: UpdateType): string => {
    switch (updateType) {
      case UpdateType.StatusChange:
        return translate("servicing:progressTimeline.statusChange");
      case UpdateType.ProgressNote:
        return translate("servicing:progressTimeline.progressNote");
      case UpdateType.SystemEvent:
        return translate("servicing:progressTimeline.systemEvent");
      default:
        return "";
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(getUiLocale(), {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <ProgressUpdateForm onSubmit={handlePostUpdate} loading={submitting} />

      {updates.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center", borderRadius: "8px", mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {translate("servicing:progressTimeline.noUpdatesYet")}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Timeline
            sx={{
              p: 0,
              "& .MuiTimelineItem-root:before": {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {updates.map((update, index) => (
              <TimelineItem key={update.id}>
                <TimelineOppositeContent
                  sx={{
                    flex: "0 0 140px",
                    py: "12px",
                    px: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                    {formatDate(update.createdDate)}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={getUpdateColor(update.updateType)} variant="outlined" sx={{ p: "4px" }}>
                    {getUpdateIcon(update.updateType)}
                  </TimelineDot>
                  {index < updates.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: update.updateType === UpdateType.StatusChange ? "#E8F5E9" : "#F5F5F5",
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: update.updateType === UpdateType.StatusChange ? "#C8E6C9" : "#E0E0E0",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "13px" }}>
                        {update.authorName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "10px",
                          backgroundColor: update.updateType === UpdateType.StatusChange ? "#4CAF50" : "#9E9E9E",
                          color: "#fff",
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                        }}
                      >
                        {getUpdateTypeLabel(update.updateType)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: "13px", color: "#424242" }}>
                      {update.message}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>
      )}
    </Box>
  );
};

export default ProviderProgressTimeline;
